<template>
  <div class="detail">
    <header class="topbar">
      <div class="topbar-inner">
        <button class="back" @click="goBack"><nricon name="arrow-left" /> 返回书城</button>
      </div>
    </header>

    <div v-if="loading" class="state"><nricon name="loading" /></div>

    <div v-else-if="notFound" class="state">
      <nricon name="warning-outline" />
      <p>这本书不存在，或你没有权限查看</p>
      <button class="btn btn--primary btn--sm" @click="$router.push('/')">回到书城</button>
    </div>

    <main v-else class="wrap">
      <section class="hero">
        <div class="hero-cover" :class="['g-' + tintIndex, { 'audio-cover': book.mediaType === 'AUDIO' }]">
          <protected-cover v-if="book.coverImage && !coverBroken" :src="book.coverImage" :alt="displayTitle" :fit="book.mediaType === 'AUDIO' ? 'contain' : 'cover'" @error="coverBroken = true" />
          <div v-else class="cover-gen">
            <span class="cg-title">{{ displayTitle }}</span>
            <span class="cg-rule"></span>
            <span class="cg-author">{{ book.author || '佚名' }}</span>
          </div>
        </div>

        <div class="hero-info">
          <h1 class="h-title">{{ displayTitle }}</h1>
          <div class="h-author">{{ book.author || '佚名' }} <span>著</span></div>

          <div class="h-chips">
            <span v-if="book.category" class="chip"><nricon name="collection-tag" /> {{ book.category }}</span>
            <span class="chip"><nricon name="document" /> {{ mediaSummary }}</span>
            <span class="chip" :class="{ pub: book.visibility === 'PUBLIC' }">
              <nricon :name="book.visibility === 'PUBLIC' ? 'view' : 'lock'" />
              {{ book.visibility === 'PUBLIC' ? '公开' : '私有' }}
            </span>
          </div>

          <div v-if="hasProgress" class="h-progress">
            <div class="hp-top">
              <span class="hp-chapter">{{ progressLabel }}</span>
              <span class="hp-pct">{{ progressPct }}%</span>
            </div>
            <div class="hp-bar"><span :style="{ width: progressPct + '%' }"></span></div>
          </div>

          <div v-if="tagList.length" class="h-tags">
            <span v-for="t in tagList" :key="t" class="tag">{{ t }}</span>
          </div>

          <div class="h-actions">
            <button class="btn btn--primary" @click="startReading">
              <nricon name="video-play" /> {{ book.mediaType === 'AUDIO' ? (hasProgress ? '继续聆听' : '开始聆听') : (hasProgress ? '继续阅读' : '开始阅读') }}
            </button>
            <button v-if="chapters.length" class="btn" @click="scrollToChapters">
              <nricon name="notebook-1" /> 目录
            </button>
            <button v-if="book.originalFile" class="btn" :disabled="downloading" @click="downloadOriginal">
              <nricon name="download" /> {{ downloading ? '下载中…' : '下载原文' }}
            </button>
            <button v-if="isLoggedIn && (!isOwner || book.visibility === 'PUBLIC')" class="btn" :class="{ 'is-fav': isFavorited }" :disabled="favLoading" @click="toggleFavorite">
              <nricon :name="isFavorited ? 'star-on' : 'star-off'" /> {{ isFavorited ? '已在书架' : '加入书架' }}
            </button>
            <template v-if="isOwner">
              <button class="btn" @click="editVisible = true"><nricon name="edit" /> 编辑</button>
              <button class="btn danger" @click="handleDelete"><nricon name="delete" /> 删除</button>
            </template>
          </div>
        </div>
      </section>

      <section v-if="book.description" class="card">
        <div class="section-heading"><h2 class="card-title">内容简介</h2></div>
        <p class="desc" :class="{ collapsed: !descriptionExpanded }">{{ book.description }}</p>
        <button v-if="book.description.length > 160" class="desc-toggle" type="button" :aria-expanded="descriptionExpanded ? 'true' : 'false'" @click="descriptionExpanded = !descriptionExpanded">{{ descriptionExpanded ? '收起简介' : '展开简介' }} <nricon :name="descriptionExpanded ? 'arrow-down' : 'arrow-right'" :size="14" /></button>
      </section>

      <section v-if="book.mediaType === 'AUDIO'" class="card audio-preview">
        <div class="section-heading"><h2 class="card-title">音轨目录</h2><span class="ct-count">{{ audioTracks.length || book.trackCount || 0 }} 条音轨</span></div>
        <div v-if="audioTracksLoading" class="ch-empty">正在载入音轨…</div>
        <div v-else-if="audioTracksError" class="ch-empty audio-error">音轨目录暂时无法载入 <button class="text-action" type="button" @click="loadAudioTracks">重试</button></div>
        <div v-else class="audio-track-preview">
          <div v-for="track in audioTracks.slice(0, 8)" :key="track.id"><nricon name="headphones" :size="15"/><span>{{ track.title }}</span><small>{{ formatDuration(track.durationSeconds) }}</small></div>
          <p v-if="audioTracks.length > 8">还有 {{ audioTracks.length - 8 }} 条音轨</p>
          <div v-if="!audioTracks.length" class="ch-empty">暂无音轨</div>
        </div>
      </section>

      <section v-if="book.mediaType !== 'PDF' && book.mediaType !== 'AUDIO'" ref="chaptersCard" class="card">
        <div class="section-heading chapter-heading"><div><h2 class="card-title">章节目录</h2></div><span class="ct-count">共 {{ chapters.length }} 章</span></div>
        <div v-if="!chapters.length" class="ch-empty">暂无章节</div>
        <div v-else class="ch-list-wrap" :style="{ height: chListHeight }">
          <virtual-list :items="chapters" :item-height="44" :columns="chCols" :gap="20" @range="onRange">
            <template #default="{ item }">
              <div
                v-if="item"
                class="ch-item" :class="{ current: progress && progress.chapterId === item.id }"
                @click="openChapter(item)"
              >
                <span class="ch-name">{{ item.title }}</span>
                <nricon v-if="progress && progress.chapterId === item.id" class="ch-flag" name="position" />
              </div>
              <div v-else class="ch-item ch-loading"><span class="ch-name">加载中…</span></div>
            </template>
          </virtual-list>
        </div>
      </section>
    </main>

    <book-editor
      v-if="editVisible"
      :visible.sync="editVisible"
      :book="book || {}"
      @success="onEditSuccess"
      @close="editVisible = false"
    />
  </div>
</template>

<script>
import api from '@/api'
import BookEditor from '@/components/book/BookEditor.vue'
import VirtualList from '@/components/VirtualList.vue'
import ProtectedCover from '@/components/common/ProtectedCover.vue'
import { cleanTitle } from '@/utils/title'

export default {
  name: 'BookDetail',
  components: { BookEditor, VirtualList, ProtectedCover },
  props: {
    bookId: { type: Number, required: true }
  },
  data() {
    return {
      book: null,
      coverBroken: false,
      audioTracks: [],
      chapters: [],
      progress: null,
      loading: true,
      notFound: false,
      editVisible: false,
      isFavorited: false,
      favLoading: false,
      chCols: window.innerWidth <= 768 ? 1 : 2,
      chapterTotal: 0,
      pageSize: 100,
      downloading: false,
      descriptionExpanded: false,
      audioTracksLoading: false,
      audioTracksError: false
    }
  },
  computed: {
    displayTitle() {
      return cleanTitle(this.book && this.book.title) || '未命名'
    },
    tintIndex() {
      const s = this.displayTitle || ''
      let sum = 0
      for (let i = 0; i < s.length; i++) sum += s.charCodeAt(i)
      return sum % 8
    },
    isLoggedIn() { return !!this.$store.state.user },
    currentUser() { return this.$store.state.user },
    isOwner() {
      return this.isLoggedIn && this.book && this.currentUser && this.book.userId === this.currentUser.id
    },
    hasProgress() {
      return !!(this.progress && (this.progress.chapterId || this.progress.pageIndex != null || this.progress.trackId || this.progress.positionSeconds > 0))
    },
    mediaSummary() {
      if (this.book && this.book.mediaType === 'PDF') return `${this.book.totalPages || this.progress && this.progress.totalPages || '—'} 页 PDF`
      if (this.book && this.book.mediaType === 'AUDIO') return `${this.book.trackCount || '—'} 条音轨`
      return `${this.chapterTotal} 章`
    },
    progressLabel() {
      if (this.book && this.book.mediaType === 'PDF' && this.progress && this.progress.pageIndex != null) {
        return `读到 · 第 ${this.progress.pageIndex + 1} 页`
      }
      if (this.book && this.book.mediaType === 'AUDIO' && this.progress) {
        const title = this.progress.trackTitle || `第 ${(Number(this.progress.trackIndex) || 0) + 1} 条音轨`
        return `听到 · ${title} · ${this.formatDuration(this.progress.positionSeconds)}`
      }
      return `读到 · ${this.lastChapterTitle || '第 ' + this.progressPct + '%'}`
    },
    progressPct() {
      const progress = this.progress || {}
      let p = progress.percentage
      if (this.book && this.book.mediaType === 'PDF' && progress.totalPages) p = ((Number(progress.pageIndex || 0) + 1) / progress.totalPages)
      if (this.book && this.book.mediaType === 'AUDIO' && progress.totalDurationSeconds) p = Number(progress.overallPositionSeconds || 0) / progress.totalDurationSeconds
      return Math.max(0, Math.min(100, Math.round((Number(p) || 0) * 100)))
    },
    lastChapterTitle() {
      return this.progress && this.progress.chapterTitle ? this.progress.chapterTitle : ''
    },
    // 后端 tags 存逗号分隔字符串，这里拆成去空去重的标签数组
    tagList() {
      const raw = this.book && this.book.tags
      if (!raw) return []
      const seen = []
      String(raw).split(',').forEach(t => {
        const s = t.trim()
        if (s && seen.indexOf(s) === -1) seen.push(s)
      })
      return seen
    },
    // 目录容器高度：小书自适应、超长封顶 460 内部滚动
    chListHeight() {
      const rows = Math.ceil(this.chapters.length / this.chCols)
      return Math.min(460, Math.max(1, rows) * 44) + 'px'
    }
  },
  watch: {
    bookId() {
      this.coverBroken = false
      this.audioTracks = []
      this.progress = null
      this.load()
    },
    // 列/尺寸变化后重新评估首屏应加载的页
    chCols() {
      this.$nextTick(() => {
        if (this.chapters.length) this.onRange({ start: 0, end: Math.min(this.chapters.length, this.pageSize) })
      })
    }
  },
  methods: {
    async load() {
      this.loading = true
      this.notFound = false
      try {
        const { data: book } = await api.book.getBookDetail(this.bookId)
        this.book = book
        this.coverBroken = false
        // 首页章节切片 + 进度并行；章节走分页（应对上万章），进度失败不影响主体
        const [sliceRes, progressRes] = await Promise.all([
          book.mediaType === 'PDF' || book.mediaType === 'AUDIO'
            ? Promise.resolve({ data: { total: 0, list: [] } })
            : api.book.getBookChaptersSlice(this.bookId, 0, this.pageSize).catch(() => ({ data: { total: 0, list: [] } })),
          api.progress.getBookProgress(this.bookId).catch(() => ({ data: null }))
        ])
        const total = Number(sliceRes.data.total) || 0
        this.chapterTotal = total
        this._pageState = new Set()
        // 预置成 total 长度的稀疏数组，虚拟列表按可视区懒填后续页
        this.chapters = new Array(total)
        this.fillPage(0, sliceRes.data.list || [])
        this.progress = progressRes.data || null
        if (book.mediaType === 'AUDIO') this.loadAudioTracks()
        // 登录用户查是否已收藏（本人也能收藏自己的书，用于把已公开的书加回“我的书库”）
        if (this.isLoggedIn) {
          try {
            const { data: ids } = await api.favorite.ids()
            this.isFavorited = Array.isArray(ids) && ids.map(Number).includes(Number(this.bookId))
          } catch (e) { /* ignore */ }
        }
      } catch (e) {
        this.notFound = true
      } finally {
        this.loading = false
      }
    },
    async toggleFavorite() {
      this.favLoading = true
      try {
        if (this.isFavorited) {
          await api.favorite.remove(this.bookId)
          this.isFavorited = false
          this.$message.success('已移出书架')
        } else {
          await api.favorite.add(this.bookId)
          this.isFavorited = true
          this.$message.success('已加入书架')
        }
      } catch (e) {
        this.$message.error('操作失败')
      } finally {
        this.favLoading = false
      }
    },
    async loadAudioTracks() {
      this.audioTracksLoading = true; this.audioTracksError = false
      try {
        const { data } = await api.book.getAudioTracks(this.bookId)
        this.audioTracks = data || []
      } catch (error) { this.audioTracksError = true }
      finally { this.audioTracksLoading = false }
    },
    formatDuration(value) {
      const seconds = Math.max(0, Math.floor(Number(value) || 0))
      if (!seconds) return '时长未知'
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remainder = seconds % 60
      return hours ? `${hours}:${String(minutes).padStart(2, '0')}:${String(remainder).padStart(2, '0')}` : `${minutes}:${String(remainder).padStart(2, '0')}`
    },
    startReading() {
      const mediaType = this.book && this.book.mediaType
      this.$router.push(mediaType === 'PDF'
        ? { name: 'PdfReader', params: { bookId: String(this.bookId) }, query: this.progress && this.progress.pageIndex != null ? { page: String(this.progress.pageIndex + 1) } : {} }
        : mediaType === 'AUDIO'
          ? { name: 'Listen', params: { bookId: String(this.bookId) } }
          : { name: 'Reader', params: { bookId: String(this.bookId) } })
    },
    openChapter(ch) {
      if (!ch) return
      this.$router.push({ name: 'Reader', params: { bookId: String(this.bookId) }, query: { chapterId: String(ch.id) } })
    },
    // 把某页数据填入稀疏数组（splice 保证 Vue 响应式）
    fillPage(pageIndex, list) {
      if (!this._pageState) this._pageState = new Set()
      this._pageState.add(pageIndex)
      const offset = pageIndex * this.pageSize
      if (list && list.length) this.chapters.splice(offset, list.length, ...list)
    },
    // 虚拟列表可视区间变化 → 按需加载覆盖到的分页
    onRange({ start, end }) {
      if (!this.chapterTotal) return
      const firstPage = Math.floor(start / this.pageSize)
      const lastPage = Math.floor(Math.max(start, end - 1) / this.pageSize)
      for (let p = firstPage; p <= lastPage; p++) {
        if (this._pageState && this._pageState.has(p)) continue
        this.loadPage(p)
      }
    },
    async loadPage(p) {
      if (!this._pageState) this._pageState = new Set()
      this._pageState.add(p) // 先占位，防并发重复请求
      try {
        const { data } = await api.book.getBookChaptersSlice(this.bookId, p * this.pageSize, this.pageSize)
        const offset = Number.isFinite(Number(data.offset)) ? Number(data.offset) : p * this.pageSize
        const list = data.list || []
        if (list.length) this.chapters.splice(offset, list.length, ...list)
      } catch (e) {
        this._pageState.delete(p) // 失败允许后续重试
      }
    },
    async downloadOriginal() {
      if (this.downloading) return
      this.downloading = true
      try {
        const res = await api.book.downloadOriginal(this.bookId)
        const fileName = this.resolveDownloadName(res.headers && res.headers['content-disposition'])
        const url = window.URL.createObjectURL(new Blob([res.data]))
        const a = document.createElement('a')
        a.href = url
        a.download = fileName
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } catch (e) {
        this.$message.error('下载失败')
      } finally {
        this.downloading = false
      }
    },
    // 下载文件名：优先响应头 filename*（UTF-8），退化到书籍 originalName / 书名
    resolveDownloadName(disposition) {
      if (disposition) {
        const star = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
        if (star && star[1]) {
          try { return decodeURIComponent(star[1]) } catch (e) { /* ignore */ }
        }
      }
      if (this.book && this.book.originalName) return this.book.originalName
      return (this.displayTitle || 'book') + '.txt'
    },
    scrollToChapters() {
      const el = this.$refs.chaptersCard
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    onResize() {
      this.chCols = window.innerWidth <= 768 ? 1 : 2
    },
    goBack() {
      if (window.history.length > 1) this.$router.back()
      else this.$router.push('/')
    },
    onEditSuccess() {
      this.editVisible = false
      this.load()
    },
    async handleDelete() {
      try {
        await this.$confirm('确定要删除这本书吗？', '提示', {
          confirmButtonText: '确定', cancelButtonText: '取消', type: 'warning'
        })
        await api.book.deleteBook(this.bookId)
        this.$message.success('删除成功')
        this.goBack()
      } catch (e) {
        if (e !== 'cancel') this.$message.error('删除失败')
      }
    }
  },
  created() {
    this._pageState = new Set() // 已加载/在加载的分页页码（非响应式）
    this.load()
    window.addEventListener('resize', this.onResize)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  }
}
</script>

<style lang="scss" scoped>
.detail {
  min-height: 100vh;
  background: var(--nr-bg);
  padding-bottom: 48px;
}

.topbar {
  position: sticky;
  top: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: var(--nr-surface);
  border-bottom: 1px solid var(--nr-border);

  .back {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    border: none;
    background: transparent;
    color: var(--nr-text-2);
    font-size: 14px;
    cursor: pointer;
    &:hover { color: var(--nr-accent); }
  }
  .brand {
    display: flex; align-items: center; gap: 6px;
    font-size: 16px; font-weight: 500; color: var(--nr-text);
    cursor: pointer;
    i { color: var(--nr-accent); font-size: 19px; }
  }
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 60vh;
  color: var(--nr-text-3);
  i { font-size: 40px; }
  p { font-size: 14px; margin: 0; }
}

.wrap {
  max-width: 900px;
  margin: 0 auto;
  padding: 24px 20px 0;
}

.hero {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
}

.hero-cover {
  position: relative;
  width: 190px;
  flex-shrink: 0;
  aspect-ratio: 3 / 4;
  border-radius: var(--nr-radius-lg);
  overflow: hidden;
  box-shadow: var(--nr-shadow-hover);

  img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }

  &::after {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 9px;
    background: linear-gradient(to right, rgba(0, 0, 0, 0.24), rgba(0, 0, 0, 0.04) 40%, rgba(255, 255, 255, 0.1) 42%, rgba(0, 0, 0, 0));
    z-index: 2;
  }
}
.hero-cover.audio-cover { background: var(--nr-surface-2); box-shadow: var(--nr-shadow); }
.hero-cover.audio-cover img { object-fit: contain; }

.cover-gen {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding: 22px 18px 18px;
  color: #fff;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    right: -40px; top: -40px;
    width: 150px; height: 150px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.14);
  }
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 55%, rgba(0, 0, 0, 0.22));
  }

  .cg-title {
    position: relative;
    z-index: 1;
    font-family: var(--nr-font-display);
    font-size: 22px;
    font-weight: 700;
    line-height: 1.34;
    letter-spacing: 0.5px;
    display: -webkit-box;
    -webkit-line-clamp: 4;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-shadow: 0 1px 3px rgba(0, 0, 0, 0.22);
  }
  .cg-rule { position: relative; z-index: 1; width: 30px; height: 3px; margin: 12px 0 auto; background: rgba(255, 255, 255, 0.75); border-radius: 2px; }
  .cg-author { position: relative; z-index: 1; margin-top: auto; font-size: 13px; color: rgba(255, 255, 255, 0.82); }
}

.hero-cover.g-0 { background: linear-gradient(150deg, #5b6ef5, #7c3aed); }
.hero-cover.g-1 { background: linear-gradient(150deg, #0ea5a3, #0f766e); }
.hero-cover.g-2 { background: linear-gradient(150deg, #f43f5e, #f97316); }
.hero-cover.g-3 { background: linear-gradient(150deg, #0ea5e9, #2563eb); }
.hero-cover.g-4 { background: linear-gradient(150deg, #f59e0b, #dc2626); }
.hero-cover.g-5 { background: linear-gradient(150deg, #d946ef, #7c3aed); }
.hero-cover.g-6 { background: linear-gradient(150deg, #475569, #0891b2); }
.hero-cover.g-7 { background: linear-gradient(150deg, #22c55e, #4d7c0f); }

.hero-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;

  .h-title {
    margin: 4px 0 0;
    font-size: 26px;
    font-weight: 600;
    color: var(--nr-text);
    line-height: 1.3;
  }
  .h-author { margin-top: 6px; font-size: 15px; color: var(--nr-text-2); }

  .h-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin: 16px 0;
    .chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 3px 10px;
      font-size: 12px;
      border-radius: 20px;
      background: var(--nr-surface-2);
      color: var(--nr-text-2);
      &.pub { background: var(--nr-accent-bg); color: var(--nr-accent-text); }
    }
  }

  .h-progress {
    margin: 2px 0 18px;
    max-width: 420px;
    .hp-top { display: flex; justify-content: space-between; font-size: 12px; color: var(--nr-text-3); margin-bottom: 6px; }
    .hp-chapter { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; margin-right: 10px; }
    .hp-bar {
      height: 6px; background: var(--nr-surface-2); border-radius: 3px; overflow: hidden;
      span { display: block; height: 100%; background: var(--nr-accent); border-radius: 3px; }
    }
  }

  .h-actions {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    margin-top: auto;
    .btn.danger:hover { color: #e24b4a; border-color: #e24b4a; }
    .btn.is-fav { color: var(--nr-accent); border-color: var(--nr-accent); }
  }
}

.card {
  background: var(--nr-surface);
  border: 1px solid var(--nr-border);
  border-radius: var(--nr-radius-lg);
  padding: 18px 20px;
  margin-bottom: 18px;

  .card-title {
    margin: 0 0 12px;
    font-size: 15px;
    font-weight: 600;
    color: var(--nr-text);
    .ct-count { font-size: 12px; font-weight: 400; color: var(--nr-text-3); margin-left: 6px; }
  }
  .desc { margin: 0; font-size: 14px; line-height: 1.8; color: var(--nr-text-2); white-space: pre-wrap; }
}

.ch-empty { color: var(--nr-text-3); font-size: 14px; padding: 8px 0; }

.ch-list-wrap { position: relative; width: 100%; }

.ch-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  height: 44px;
  padding: 0 10px;
  box-sizing: border-box;
  font-size: 14px;
  color: var(--nr-text-2);
  cursor: pointer;
  border-bottom: 1px solid var(--nr-border);
  &:hover { background: var(--nr-surface-2); color: var(--nr-text); }
  &.current { color: var(--nr-accent-text); background: var(--nr-accent-bg); }

  .ch-name { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .ch-flag { flex-shrink: 0; }
}

.ch-item.ch-loading {
  color: var(--nr-text-3);
  cursor: default;
  &:hover { background: transparent; color: var(--nr-text-3); }
}

@media screen and (max-width: 768px) {
  .wrap { padding: 18px 14px 0; }
  .hero { flex-direction: column; align-items: center; gap: 18px; }
  .hero-cover { width: 150px; }
  .hero-info { width: 100%; }
  .hero-info .h-title { font-size: 22px; text-align: center; }
  .hero-info .h-author { text-align: center; }
  .hero-info .h-chips { justify-content: center; }
  .hero-info .h-actions { justify-content: center; }
}
</style>

<style lang="scss" scoped>
.detail { padding-bottom: 76px; background: transparent; }

.topbar {
  padding: 0;
  border-bottom: 1px solid color-mix(in srgb, var(--nr-border) 80%, transparent);
  background: color-mix(in srgb, var(--nr-surface) 90%, transparent);
  backdrop-filter: blur(18px);
}

.topbar-inner {
  display: flex;
  width: min(100% - 40px, var(--nr-content-width));
  min-height: 68px;
  align-items: center;
  justify-content: space-between;
  margin-inline: auto;
}

.topbar .back {
  min-height: 44px;
  padding: 0 10px 0 0;
  font-weight: 600;
  transition: color 180ms ease;
}

.topbar .brand {
  display: inline-flex;
  min-height: 44px;
  align-items: center;
  gap: 9px;
  padding: 0;
  color: var(--nr-text);
  border: 0;
  background: transparent;
  font-family: var(--nr-font-display);
  font-size: 18px;
  font-weight: 600;

  .brand-mark { display: grid; width: 32px; height: 32px; place-items: center; color: var(--nr-on-accent); border-radius: 50% 50% 50% 10px; background: var(--nr-accent); }
  i { color: inherit; font-size: 17px; }
}

.wrap { max-width: 1040px; padding-top: 38px; }

.hero {
  display: flex;
  align-items: center;
  gap: clamp(30px, 5vw, 56px);
  margin-bottom: 30px;
  padding: clamp(28px, 4vw, 44px);
  border: 1px solid var(--nr-border);
  border-radius: var(--nr-radius-lg);
  background: var(--nr-surface);
}

.hero-cover {
  flex-shrink: 0;
  width: clamp(190px, 22vw, 224px);
  border-radius: var(--nr-radius);
  overflow: hidden;
  box-shadow: var(--nr-shadow-hover);
}

.cover-gen {
  padding: 30px 24px 22px;
  .cg-title { font-family: var(--nr-font-display); font-size: 27px; font-weight: 600; text-shadow: none; }
  .cg-rule { width: 27px; height: 2px; border-radius: 0; }
}

.hero-cover.g-0 { background: linear-gradient(145deg, #485c68, #26343e); }
.hero-cover.g-1 { background: linear-gradient(145deg, #477064, #24463e); }
.hero-cover.g-2 { background: linear-gradient(145deg, #9b5945, #643426); }
.hero-cover.g-3 { background: linear-gradient(145deg, #526985, #2d415d); }
.hero-cover.g-4 { background: linear-gradient(145deg, #b0753e, #74451f); }
.hero-cover.g-5 { background: linear-gradient(145deg, #7b5878, #493247); }
.hero-cover.g-6 { background: linear-gradient(145deg, #5f6266, #33363a); }
.hero-cover.g-7 { background: linear-gradient(145deg, #718056, #3d4c2d); }

.hero-info {
  min-width: 0;
  flex: 1;
  align-self: center;

  .h-title {
    margin: 0;
    font-family: var(--nr-font-display);
    font-size: clamp(30px, 4vw, 44px);
    font-weight: 700;
    line-height: 1.12;
    letter-spacing: -.02em;
  }

  .h-author {
    margin-top: 12px;
    color: var(--nr-text-2);
    font-size: 15px;
    span { margin-left: 3px; color: var(--nr-text-3); font-size: 12px; }
  }

  .h-chips {
    margin: 20px 0;
    .chip { min-height: 28px; padding: 4px 11px; border: 1px solid var(--nr-border); border-radius: 6px; background: transparent; color: var(--nr-text-2); font-size: 12px; }
    .chip.pub { border-color: transparent; background: var(--nr-accent-bg); color: var(--nr-accent-text); }
  }

  .h-progress { max-width: 520px; margin-bottom: 22px; }
  .h-progress .hp-bar { height: 4px; background: var(--nr-surface-3); }
  .h-actions { gap: 9px; }

  .h-tags {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: -8px 0 22px;
    .tag {
      padding: 3px 9px;
      border: 1px solid var(--nr-border);
      border-radius: 5px;
      background: var(--nr-surface-2);
      color: var(--nr-text-3);
      font-size: 12px;
      line-height: 1.5;
    }
  }
}

.card {
  margin-bottom: 22px;
  padding: clamp(24px, 4vw, 38px);
  border-radius: var(--nr-radius-lg);
  background: var(--nr-surface);
  box-shadow: none;
  scroll-margin-top: 86px;

  .card-title {
    margin: 0;
    font-size: 19px;
    font-weight: 700;
    letter-spacing: 0;
  }

  .desc { margin: 18px 0 0; color: var(--nr-text-2); font-size: 15px; line-height: 1.9; }
  .desc.collapsed { max-height: 7.6em; overflow: hidden; }
  .desc-toggle { display: inline-flex; min-height: 40px; align-items: center; gap: 5px; margin-top: 5px; padding: 0; color: var(--nr-accent-text); border: 0; background: transparent; font-size: 12px; font-weight: 650; cursor: pointer; }
}

.section-heading { padding-bottom: 16px; border-bottom: 1px solid var(--nr-border); }
.chapter-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 20px; }
.chapter-heading .ct-count { padding-bottom: 3px; color: var(--nr-text-3); font-size: 12px; }

.ch-list-wrap { margin-top: 10px; }
.ch-item {
  position: relative;
  height: 44px;
  padding: 0 12px 0 24px;
  border-bottom-color: color-mix(in srgb, var(--nr-border) 70%, transparent);
  transition: color 160ms ease, background 160ms ease;

  &::before { content: ''; position: absolute; left: 8px; width: 4px; height: 4px; border-radius: 50%; background: var(--nr-border-strong); }
  &:hover { color: var(--nr-accent-text); background: var(--nr-accent-bg); }
  &.current { color: var(--nr-accent-text); background: var(--nr-accent-bg); font-weight: 600; }
  &.current::before { background: var(--nr-accent); }
}

.state { min-height: 70vh; }

@media (max-width: 768px) {
  .detail { padding-bottom: 44px; }
  .topbar-inner { width: min(100% - 28px, var(--nr-content-width)); min-height: 62px; }
  .topbar .brand span:last-child { display: none; }
  .wrap { padding: 22px 14px 0; }
  .hero { gap: 23px; padding: 28px 20px; border-radius: var(--nr-radius-lg); }
  .hero::after { display: none; }
  .hero-cover { width: 146px; }
  .cover-gen { padding: 20px 15px; }
  .cover-gen .cg-title { font-size: 19px; }
  .hero-info .h-title { margin-top: 8px; font-size: 34px; text-align: center; }
  .hero-info .h-author { text-align: center; }
  .hero-info .nr-kicker { text-align: center; }
  .hero-info .h-chips { margin: 16px 0; }
  .hero-info .h-tags { justify-content: center; margin: -6px 0 16px; }
  .hero-info .h-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .hero-info .h-actions .btn:first-child { grid-column: 1 / -1; }
  .card { padding: 24px 18px; }
  .card .card-title { font-size: 24px; }
  .card .desc { font-size: 15px; }
}
</style>
